/**
 * Account, „Můj účet". Úprava jména/firmy (profiles) + změna hesla (Supabase auth).
 * Roli ani e-mail tu měnit nelze (role jen admin přes backend; e-mail vyžaduje SMTP).
 */
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../auth/AuthProvider';

const ROLE_LABEL = { client: 'Klient', team: 'Tým', admin: 'Admin' };

const card = {
  background: 'var(--purple-dark, #FFFFFF)', border: '1px solid rgba(155,89,212,0.18)',
  borderRadius: 14, padding: '1.5rem', marginBottom: '1.25rem',
};
const label = { display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0 0 0.3rem' };
const input = {
  width: '100%', padding: '0.7rem 0.9rem', boxSizing: 'border-box', marginBottom: '0.9rem',
  background: 'var(--purple-dark)', color: 'var(--star-white)', border: '1px solid rgba(155,89,212,0.25)',
  borderRadius: 8, fontSize: '0.95rem',
};
const button = {
  padding: '0.6rem 1.2rem', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
  fontSize: '0.85rem', letterSpacing: '0.05em', textTransform: 'uppercase',
  background: 'var(--purple-glow)', color: 'white',
};

function Msg({ m }) {
  if (!m) return null;
  return <div style={{ color: m.ok ? 'rgba(47,143,91,0.45)' : 'rgba(192,69,63,0.45)', fontSize: '0.85rem', marginBottom: '0.6rem' }}>{m.text}</div>;
}

export function Account() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ full_name: '', company_name: '', role: 'client' });
  const [pmsg, setPmsg] = useState(null);
  const [psaving, setPsaving] = useState(false);

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwmsg, setPwmsg] = useState(null);
  const [pwsaving, setPwsaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('full_name,company_name,role').eq('id', user.id).maybeSingle()
      .then(({ data }) => { if (data) setProfile({ full_name: data.full_name || '', company_name: data.company_name || '', role: data.role || 'client' }); });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault(); setPsaving(true); setPmsg(null);
    const { error } = await supabase.from('profiles')
      .update({ full_name: profile.full_name, company_name: profile.company_name })
      .eq('id', user.id);
    setPsaving(false);
    setPmsg(error ? { ok: false, text: error.message } : { ok: true, text: 'Profil uložen.' });
  };

  const changePassword = async (e) => {
    e.preventDefault(); setPwmsg(null);
    if (pw1.length < 6) return setPwmsg({ ok: false, text: 'Heslo musí mít aspoň 6 znaků.' });
    if (pw1 !== pw2) return setPwmsg({ ok: false, text: 'Hesla se neshodují.' });
    setPwsaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setPwsaving(false);
    if (error) return setPwmsg({ ok: false, text: error.message });
    setPw1(''); setPw2(''); setPwmsg({ ok: true, text: 'Heslo změněno.' });
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '1.5rem 1rem', fontFamily: "'Inter', sans-serif" }}>
      <h2 style={{ marginTop: 0, fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.4rem' }}>
        Můj účet
      </h2>

      <div style={card}>
        <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
          <span style={{ color: 'var(--star-white)' }}>{user?.email}</span>
          <span style={{ marginLeft: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 999,
            border: '1px solid rgba(155,89,212,0.3)', fontSize: '0.72rem' }}>
            {ROLE_LABEL[profile.role] || profile.role}
          </span>
        </div>
        <form onSubmit={saveProfile}>
          <label style={label}>Jméno a příjmení</label>
          <input style={input} value={profile.full_name} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))} />
          <label style={label}>Firma</label>
          <input style={input} value={profile.company_name} onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))} />
          <Msg m={pmsg} />
          <button type="submit" style={{ ...button, opacity: psaving ? 0.6 : 1 }} disabled={psaving}>
            {psaving ? '…' : 'Uložit profil'}
          </button>
        </form>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: 'var(--star-white)', fontSize: '1.05rem' }}>Změna hesla</h3>
        <form onSubmit={changePassword}>
          <label style={label}>Nové heslo</label>
          <input style={input} type="password" value={pw1} onChange={e => setPw1(e.target.value)} minLength={6} />
          <label style={label}>Nové heslo znovu</label>
          <input style={input} type="password" value={pw2} onChange={e => setPw2(e.target.value)} minLength={6} />
          <Msg m={pwmsg} />
          <button type="submit" style={{ ...button, opacity: pwsaving ? 0.6 : 1 }} disabled={pwsaving}>
            {pwsaving ? '…' : 'Změnit heslo'}
          </button>
        </form>
      </div>
    </div>
  );
}
